/** What Home reads: the summary of filings and counts (`GET /home/summary`, wave 3 B1) and the
 * action-item feed (`GET /work/items`, ADR-0164).
 *
 * **Neither builds a sentence about a regulation.** The summary carries codes (`next_step`,
 * `verdict`, `unavailable_reason`) and exact figures; the words are the screen's, in
 * `screens/homeWords.ts`. And **null is not zero**: a count a role is not asked is `null`, and a
 * filing that could not be built is a row with `available: false`, so this module passes both
 * through unchanged. A `?? 0` here would be the defect `data ?? []` was.
 *
 * Both are plain reads that throw on failure (`unwrap`), so `isPending`/`error` are the three
 * states Home draws: still reading, could not read, read.
 */

import { useQuery } from "@tanstack/react-query";

import { api } from "./client";
import { unwrap } from "./queries";
import type { components } from "./schema";
import type { Scope2Method } from "./queries";

export type HomeSummary = components["schemas"]["SummaryOut"];
export type FilingRow = components["schemas"]["FilingRowOut"];
export type WorkItem = components["schemas"]["WorkItemOut"];
export type WorkItems = components["schemas"]["WorkItemsOut"];

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

/** A response is checked, not trusted: a stub or a proxy answering `[]` or `{}` is a *failed* read,
 * drawn as one, and never an empty Home (the shape `homeChanges.readHomeChanges` already enforces). */
function readSummary(value: unknown): HomeSummary {
  if (!isRecord(value) || !isRecord(value.counts) || !Array.isArray(value.filings)) {
    throw new Error("The summary of your filings came back in a shape this screen cannot read.");
  }
  return value as unknown as HomeSummary;
}

function readItems(value: unknown): WorkItems {
  if (
    !isRecord(value) ||
    !Array.isArray(value.items) ||
    !Array.isArray(value.notes) ||
    !isRecord(value.counts)
  ) {
    throw new Error("The list of what needs you came back in a shape this screen cannot read.");
  }
  return value as unknown as WorkItems;
}

export const summaryKeys = {
  summary: (start: string, end: string, method: Scope2Method) =>
    ["home", "summary", start, end, method] as const,
  items: (mine: boolean, limit: number) => ["work", "items", mine, limit] as const,
};

interface Window {
  period_start: string;
  period_end: string;
}

/** The home summary for the window in force. With no window, none is asked for: the summary then
 * has no census to read and the filings' dates are the only thing it could say, which is not what
 * Home is for. */
export function useHomeSummary(window: Window | undefined, method: Scope2Method) {
  return useQuery({
    queryKey: summaryKeys.summary(window?.period_start ?? "none", window?.period_end ?? "none", method),
    enabled: window !== undefined,
    queryFn: async (): Promise<HomeSummary> =>
      readSummary(
        unwrap(
        await api.GET("/api/v1/home/summary", {
          params: {
            query: {
              scope2_method: method,
              ...(window
                ? { period_start: window.period_start, period_end: window.period_end }
                : {}),
            },
          },
        }),
        ),
      ),
  });
}

/** The action-item feed: tasks and derived items, one envelope. `mine` narrows to the caller's own
 * (a site lead's Home); without it the feed is everything the caller's role may see. */
export function useWorkItems(options: { mine?: boolean; limit?: number } = {}) {
  const mine = options.mine === true;
  const limit = options.limit ?? 8;
  return useQuery({
    queryKey: summaryKeys.items(mine, limit),
    queryFn: async (): Promise<WorkItems> =>
      readItems(
        unwrap(
          await api.GET("/api/v1/work/items", {
            params: { query: { limit, ...(mine ? { mine: true } : {}) } },
          }),
        ),
      ),
  });
}
